import React, { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useUsers } from '../hooks/useUsers';
import { useProjects } from '../hooks/useProjects';
import { useProjectReports, DateRange } from '../hooks/useProjectReports';
import { Task } from '../types/task';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { 
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  FolderIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  UserIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  TagIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const ProjectReports: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { hasAccess } = usePermissions();
  const { users } = useUsers();
  const { projects } = useProjects();
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date()
  });
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<Task['status'][]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<Task['priority'][]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [timeData, setTimeData] = useState<any>(null);
  const [loadingTime, setLoadingTime] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showTaskList, setShowTaskList] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  const [taskTimeEntries, setTaskTimeEntries] = useState<Map<string, any[]>>(new Map());
  const [updatingEntryId, setUpdatingEntryId] = useState<string | null>(null);
  const [showBillableSummary, setShowBillableSummary] = useState(false);
  const [showTimeEntriesSummary, setShowTimeEntriesSummary] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const {
    projectStats,
    tasksByStatus,
    tasksByPriority,
    topProjects,
    getTimeTrackingData,
    getCompletedTasks,
    getFilteredTasks,
    getTasksWithPeriodHours,
    formatHours,
    formatDuration
  } = useProjectReports(dateRange, selectedUserId);

  // Időbejegyzések lekérdezése és task szerint csoportosítása
  useEffect(() => {
    const fetchTaskTimeEntries = async () => {
      if (!user?.organizationId) return;
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('../lib/firebase');
        const q = query(
          collection(db, 'timeEntries'),
          where('organizationId', '==', user.organizationId)
        );
        const snapshot = await getDocs(q);
        const entriesByTask = new Map<string, any[]>();
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          const taskId = data.taskId;
          if (!taskId) return;
          if (!entriesByTask.has(taskId)) entriesByTask.set(taskId, []);
          entriesByTask.get(taskId)!.push({ id: doc.id, ...data });
        });
        setTaskTimeEntries(entriesByTask);
      } catch (error) {
        console.error('Error fetching task time entries:', error);
      }
    };
    fetchTaskTimeEntries();
  }, [user?.organizationId]);

  // Get all unique tags from tasks
  const allTags = useMemo(() => {
    const filteredTasks = getFilteredTasks();
    const tagSet = new Set<string>();
    filteredTasks.forEach(task => {
      task.tags?.forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [getFilteredTasks]);

  // Apply advanced filters to tasks (billing-accurate: includes tasks with time entries in period)
  const filteredTaskList = useMemo(() => {
    let tasks = getTasksWithPeriodHours();

    // Filter by projects
    if (selectedProjectIds.length > 0) {
      tasks = tasks.filter(task => task.projectId && selectedProjectIds.includes(task.projectId));
    }

    // Filter by statuses
    if (selectedStatuses.length > 0) {
      tasks = tasks.filter(task => selectedStatuses.includes(task.status));
    }

    // Filter by priorities
    if (selectedPriorities.length > 0) {
      tasks = tasks.filter(task => selectedPriorities.includes(task.priority));
    }

    // Filter by tags
    if (selectedTags.length > 0) {
      tasks = tasks.filter(task => 
        task.tags?.some((tag: string) => selectedTags.includes(tag))
      );
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      tasks = tasks.filter(task =>
        task.title.toLowerCase().includes(search) ||
        task.description?.toLowerCase().includes(search) ||
        task.taskNumber?.toLowerCase().includes(search)
      );
    }

    return tasks.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
  }, [getTasksWithPeriodHours, selectedProjectIds, selectedStatuses, selectedPriorities, selectedTags, searchTerm]);

  // Számlázható órák összesítése taskonként (időszakra szűrve)
  const billableSummary = useMemo(() => {
    const rows: { taskNumber: string; title: string; hours: string; decimalHours: string; seconds: number }[] = [];
    let totalBillableSeconds = 0;

    for (const task of filteredTaskList) {
      const entries = taskTimeEntries.get(task.id) || [];
      const billableEntries = entries.filter((e: any) => {
        if (!e.isBillable) return false;
        const start = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
        return start >= dateRange.from && start <= dateRange.to;
      });
      const billableSeconds = billableEntries.reduce((sum: number, e: any) => sum + (e.duration || 0), 0);

      if (billableSeconds > 0) {
        const hours = Math.floor(billableSeconds / 3600);
        const mins = Math.floor((billableSeconds % 3600) / 60);
        rows.push({
          taskNumber: task.taskNumber || task.id.slice(0, 8),
          title: task.title,
          hours: `${hours}h ${mins}m`,
          decimalHours: (billableSeconds / 3600).toFixed(2),
          seconds: billableSeconds
        });
        totalBillableSeconds += billableSeconds;
      }
    }

    const totalHours = Math.floor(totalBillableSeconds / 3600);
    const totalMins = Math.floor((totalBillableSeconds % 3600) / 60);

    return {
      rows,
      totalBillableSeconds,
      totalLabel: `${totalHours}h ${totalMins}m`,
      totalDecimal: (totalBillableSeconds / 3600).toFixed(2)
    };
  }, [filteredTaskList, taskTimeEntries, dateRange]);

  // Vágólapra másolás
  const copyBillableSummary = async () => {
    if (billableSummary.rows.length === 0) return;
    const lines = [
      'Feladat\tLeírás\tÓra (óra:perc)\tÓra (tizedes)',
      ...billableSummary.rows.map(r => `${r.taskNumber}\t${r.title}\t${r.hours}\t${r.decimalHours}`),
      `\nÖsszesen:\t\t${billableSummary.totalLabel}\t${billableSummary.totalDecimal}`
    ];
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Másolva a vágólapra!');
    } catch {
      toast.error('A vágólapra másolás sikertelen');
    }
  };

  // Minden időbejegyzés flat listába (időszakra szűrve)
  const flatTimeEntries = useMemo(() => {
    const result: { entry: any; task: any }[] = [];
    const taskMap = new Map<string, any>();
    filteredTaskList.forEach((task: any) => taskMap.set(task.id, task));

    taskTimeEntries.forEach((entries, taskId) => {
      const task = taskMap.get(taskId);
      if (!task) return;
      entries.forEach((entry: any) => {
        const start = entry.startTime?.toDate ? entry.startTime.toDate() : new Date(entry.startTime);
        if (dateRange && (start < dateRange.from || start > dateRange.to)) return;
        if (selectedUserId && entry.userId !== selectedUserId) return;
        result.push({ entry, task });
      });
    });

    return result.sort((a, b) => {
      const aStart = a.entry.startTime?.toDate ? a.entry.startTime.toDate() : new Date(a.entry.startTime);
      const bStart = b.entry.startTime?.toDate ? b.entry.startTime.toDate() : new Date(b.entry.startTime);
      return bStart.getTime() - aStart.getTime();
    });
  }, [taskTimeEntries, filteredTaskList, dateRange, selectedUserId]);

  // Vágólapra másolás
  const copyTimeEntriesSummary = async () => {
    if (flatTimeEntries.length === 0) return;
    const lines = [
      'Feladat\tLeírás (entry)\tDátum\tKezdés\tBefejezés\tIdőtartam (óra:perc)\tSzámlázható\tFelhasználó',
      ...flatTimeEntries.map(({ entry, task }) => {
        const start = entry.startTime?.toDate ? entry.startTime.toDate() : new Date(entry.startTime);
        const end = entry.endTime?.toDate ? entry.endTime.toDate() : (entry.endTime ? new Date(entry.endTime) : null);
        const duration = entry.duration || 0;
        const hours = Math.floor(duration / 3600);
        const mins = Math.floor((duration % 3600) / 60);
        return [
          task.taskNumber || task.id.slice(0, 8),
          entry.description || '',
          format(start, 'yyyy.MM.dd', { locale: hu }),
          format(start, 'HH:mm', { locale: hu }),
          end ? format(end, 'HH:mm', { locale: hu }) : '',
          `${hours}h ${mins}m`,
          entry.isBillable ? 'Számlázható' : 'Ellenőrzés alatt',
          entry.userName || ''
        ].join('\t');
      })
    ];
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Időbejegyzések másolva a vágólapra!');
    } catch {
      toast.error('A vágólapra másolás sikertelen');
    }
  };

  // Összes időbejegyzés számlázhatóságának tömeges átkapcsolása (csak a szűrt listában)
  const bulkSetAllBillable = async () => {
    const entriesToUpdate = flatTimeEntries.filter(({ entry }) => !entry.isBillable);
    if (entriesToUpdate.length === 0) {
      toast('Minden időbejegyzés már számlázható');
      return;
    }

    setBulkUpdating(true);
    try {
      const { updateTimeEntry } = await import('../services/timeTrackingService');
      let updatedCount = 0;
      for (const { entry } of entriesToUpdate) {
        try {
          await updateTimeEntry(entry.id, { isBillable: true });
          updatedCount++;
        } catch (e) {
          console.error('Failed to update entry:', entry.id, e);
        }
      }

      // Frissítjük a helyi state-et
      setTaskTimeEntries(prev => {
        const next = new Map(prev);
        entriesToUpdate.forEach(({ entry, task }) => {
          const entries = next.get(task.id) || [];
          const updated = entries.map(e => e.id === entry.id ? { ...e, isBillable: true } : e);
          next.set(task.id, updated);
        });
        return next;
      });

      toast.success(`${updatedCount} / ${entriesToUpdate.length} időbejegyzés számlázhatóra állítva`);
    } catch (error) {
      console.error('Error bulk updating:', error);
      toast.error('Hiba történt a tömeges frissítés során');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Load time tracking data
  useEffect(() => {
    const loadTimeData = async () => {
      setLoadingTime(true);
      const data = await getTimeTrackingData();
      setTimeData(data);
      setLoadingTime(false);
    };
    loadTimeData();
  }, [dateRange, selectedUserId]);

  // Export completed tasks to CSV
  const exportCompletedTasks = () => {
    const completedTasks = getCompletedTasks();
    
    if (completedTasks.length === 0) {
      toast.error(t('projectReports.export.noData') || 'Nincs exportálható adat');
      return;
    }

    try {
      // CSV header
      const headers = [
        t('tasks.fields.taskNumber') || 'Feladat szám',
        t('tasks.fields.title') || 'Cím',
        t('tasks.fields.project') || 'Projekt',
        t('tasks.fields.assignedTo') || 'Felelős',
        t('tasks.fields.priority') || 'Prioritás',
        t('tasks.fields.dueDate') || 'Határidő',
        t('tasks.fields.completedDate') || 'Befejezve',
        t('tasks.fields.estimatedHours') || 'Becsült órák',
        t('tasks.fields.actualHours') || 'Tényleges órák'
      ];

      // CSV rows
      const rows = completedTasks.map(task => [
        task.taskNumber || '',
        `"${task.title.replace(/"/g, '""')}"`,
        `"${task.projectName || '-'}"`,
        `"${task.assignedToName || '-'}"`,
        t(`tasks.priority.${task.priority}`) || task.priority,
        task.dueDate ? new Date(task.dueDate).toLocaleDateString('hu-HU') : '-',
        task.completedDate ? new Date(task.completedDate).toLocaleDateString('hu-HU') : '-',
        task.estimatedHours || '0',
        task.actualHours || '0'
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const fileName = `elvegzett_munkak_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Success notification
      toast.success(
        `${t('projectReports.export.success') || 'Sikeres export'} (${completedTasks.length} ${t('tasks.title') || 'feladat'})`
      );
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('projectReports.export.error') || 'Hiba történt az export során');
    }
  };

  // Jogosultság ellenőrzés
  if (!hasAccess('projects')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">{t('common.errors.noPermission')}</div>
      </div>
    );
  }

  // Date range presets
  const setPresetRange = (preset: string) => {
    const now = new Date();
    let from = new Date();
    let to = new Date();

    switch (preset) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;
      case 'thisWeek':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay(), 0, 0, 0);
        to = new Date();
        break;
      case 'lastWeek':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 7, 0, 0, 0);
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 1, 23, 59, 59);
        break;
      case 'thisMonth':
        from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
        to = new Date();
        break;
      case 'lastMonth':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
        to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'thisYear':
        from = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
        to = new Date();
        break;
      default:
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0);
        to = new Date();
    }

    setDateRange({ from, to });
  };

  // Időbejegyzés számlázhatóságának átkapcsolása
  const toggleBillable = async (entryId: string, newValue: boolean, taskId: string) => {
    setUpdatingEntryId(entryId);
    try {
      const { updateTimeEntry } = await import('../services/timeTrackingService');
      await updateTimeEntry(entryId, { isBillable: newValue });
      
      // Frissítjük a helyi state-et
      setTaskTimeEntries(prev => {
        const next = new Map(prev);
        const entries = next.get(taskId) || [];
        const updated = entries.map(e => e.id === entryId ? { ...e, isBillable: newValue } : e);
        next.set(taskId, updated);
        return next;
      });

      toast.success(newValue ? 'Számlázhatóra állítva' : 'Ellenőrzés alatt');
    } catch (error) {
      console.error('Error updating billable status:', error);
      toast.error('Hiba történt a frissítés során');
    } finally {
      setUpdatingEntryId(null);
    }
  };

  return (
    <div className="space-y-3 md:space-y-6">
      {/* Header */}
      <CommonPageHeader
        title={t('projectReports.title')}
        subtitle={t('projectReports.subtitle')}
        icon={ChartBarIcon}
      />

      {/* Content */}
      <div className="w-full py-2 md:py-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-4">
          {/* Total Projects */}
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <FolderIcon className="h-8 w-8 opacity-80" />
              <span className="text-3xl font-bold">{projectStats.totalProjects}</span>
            </div>
            <p className="text-sm opacity-90">{t('projectReports.overview.totalProjects')}</p>
            <div className="mt-2 text-xs opacity-75">
              {t('projectReports.overview.activeProjects')}: {projectStats.activeProjects}
            </div>
          </div>

          {/* Total Tasks */}
          <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <CheckCircleIcon className="h-8 w-8 opacity-80" />
              <span className="text-3xl font-bold">{projectStats.totalTasks}</span>
            </div>
            <p className="text-sm opacity-90">{t('projectReports.overview.totalTasks')}</p>
            <div className="mt-2 text-xs opacity-75">
              {t('projectReports.overview.completedTasks')}: {projectStats.completedTasks}
            </div>
          </div>

          {/* Completion Rate */}
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <ChartBarIcon className="h-8 w-8 opacity-80" />
              <span className="text-3xl font-bold">{projectStats.completionRate.toFixed(1)}%</span>
            </div>
            <p className="text-sm opacity-90">{t('projectReports.overview.completionRate')}</p>
          </div>

          {/* Overdue Tasks */}
          <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <ExclamationTriangleIcon className="h-8 w-8 opacity-80" />
              <span className="text-3xl font-bold">{projectStats.overdueTasks}</span>
            </div>
            <p className="text-sm opacity-90">{t('projectReports.overview.overdueTasks')}</p>
          </div>

          {/* Completed Projects */}
          <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <CheckCircleIcon className="h-8 w-8 opacity-80" />
              <span className="text-3xl font-bold">{projectStats.completedProjects}</span>
            </div>
            <p className="text-sm opacity-90">{t('projectReports.overview.completedProjects')}</p>
          </div>
        </div>

        {/* Advanced Filters */}
        <div className="mb-3 md:mb-4 bg-white dark:bg-gray-800/50 rounded-lg md:rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Filter Header */}
          <div 
            className="p-2 md:p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-lg md:rounded-t-xl"
            onClick={() => setShowFilters(!showFilters)}
          >
            <div className="flex items-center gap-2 md:gap-3">
              <FunnelIcon className="h-4 w-4 md:h-6 md:w-6 text-primary-600 dark:text-primary-400" />
              <h3 className="text-sm md:text-lg font-semibold text-gray-900 dark:text-white">
                Részletes szűrők
              </h3>
              <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                ({filteredTaskList.length})
              </span>
            </div>
            {showFilters ? (
              <ChevronUpIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-500" />
            )}
          </div>

          {/* Filter Content */}
          {showFilters && (
            <div className="p-2 md:p-4 border-t border-gray-200 dark:border-gray-700 space-y-3 md:space-y-4">
              {/* Date Range Presets */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2 flex items-center gap-1.5 md:gap-2">
                  <CalendarIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  Időszak
                </label>
                <div className="flex flex-wrap gap-1.5 md:gap-2">
                  {['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'thisYear'].map(preset => (
                    <button
                      key={preset}
                      onClick={() => setPresetRange(preset)}
                      className="px-2 md:px-3 py-1 md:py-1.5 bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300 rounded-md md:rounded-lg hover:bg-primary-200 dark:hover:bg-primary-500/30 transition-colors text-xs md:text-sm font-medium border border-primary-200 dark:border-primary-500/30"
                    >
                      {t(`projectReports.filters.${preset}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Search */}
              <div>
                <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                  Keresés
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Feladat címe, leírása vagy száma..."
                  className="w-full px-3 md:px-4 py-1.5 md:py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg text-xs md:text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {/* User Filter */}
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2 flex items-center gap-1.5 md:gap-2">
                    <UserIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                    Felhasználó
                  </label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full px-2 md:px-3 py-1.5 md:py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg text-xs md:text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Minden felhasználó</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.displayName ? `${u.displayName} (${u.email})` : u.email}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Project Filter */}
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2 flex items-center gap-1.5 md:gap-2">
                    <FolderIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                    Projektek
                  </label>
                  <div className="space-y-2 max-h-[120px] overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md md:rounded-lg p-2 bg-white dark:bg-gray-700">
                    {projects.map(p => (
                      <label key={p.id} className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={selectedProjectIds.includes(p.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProjectIds([...selectedProjectIds, p.id]);
                            } else {
                              setSelectedProjectIds(selectedProjectIds.filter(id => id !== p.id));
                            }
                          }}
                          className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 flex-shrink-0"
                        />
                        <span className="text-xs md:text-sm text-gray-700 dark:text-gray-300 line-clamp-2" title={p.name}>
                          {p.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  {selectedProjectIds.length > 0 && (
                    <button
                      onClick={() => setSelectedProjectIds([])}
                      className="mt-1 text-xs text-red-600 dark:text-red-400 hover:underline"
                    >
                      Törlés ({selectedProjectIds.length})
                    </button>
                  )}
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                    Státusz
                  </label>
                  <div className="space-y-2">
                    {(['todo', 'in_progress', 'completed', 'cancelled'] as Task['status'][]).map(status => (
                      <label key={status} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedStatuses.includes(status)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedStatuses([...selectedStatuses, status]);
                            } else {
                              setSelectedStatuses(selectedStatuses.filter(s => s !== status));
                            }
                          }}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-xs md:text-sm text-gray-700 dark:text-gray-300">
                          {t(`tasks.status.${status}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Priority Filter */}
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2">
                    Prioritás
                  </label>
                  <div className="space-y-2">
                    {(['low', 'medium', 'high', 'urgent'] as Task['priority'][]).map(priority => (
                      <label key={priority} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedPriorities.includes(priority)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedPriorities([...selectedPriorities, priority]);
                            } else {
                              setSelectedPriorities(selectedPriorities.filter(p => p !== priority));
                            }
                          }}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-xs md:text-sm text-gray-700 dark:text-gray-300">
                          {t(`tasks.priority.${priority}`)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Tags Filter */}
              {allTags.length > 0 && (
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 md:mb-2 flex items-center gap-1.5 md:gap-2">
                    <TagIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                    Címkék
                  </label>
                  <div className="flex flex-wrap gap-1.5 md:gap-2">
                    {allTags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => {
                          if (selectedTags.includes(tag)) {
                            setSelectedTags(selectedTags.filter(t => t !== tag));
                          } else {
                            setSelectedTags([...selectedTags, tag]);
                          }
                        }}
                        className={`px-2 md:px-3 py-1 md:py-1.5 rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-colors ${
                          selectedTags.includes(tag)
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Clear Filters & Export */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 pt-3 md:pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setSelectedProjectIds([]);
                    setSelectedStatuses([]);
                    setSelectedPriorities([]);
                    setSelectedTags([]);
                    setSearchTerm('');
                    setSelectedUserId('');
                  }}
                  className="flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md md:rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">Szűrők törlése</span>
                  <span className="sm:hidden">Törlés</span>
                </button>

                <button
                  onClick={exportCompletedTasks}
                  className="flex items-center justify-center gap-1.5 md:gap-2 px-4 md:px-6 py-1.5 md:py-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white rounded-md md:rounded-lg text-xs md:text-sm font-medium transition-colors shadow-lg hover:shadow-xl"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 md:h-5 md:w-5" />
                  <span className="hidden sm:inline">Elvégzett munkák exportálása</span>
                  <span className="sm:hidden">Export</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Time Tracking */}
        {timeData && (
          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <ClockIcon className="h-5 w-5 mr-2 text-primary-600 dark:text-primary-400" />
              {t('projectReports.timeTracking.title')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatHours(timeData.totalTime)} {t('projectReports.timeTracking.hours')}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('projectReports.timeTracking.totalTracked')}
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatHours(timeData.billableTime)} {t('projectReports.timeTracking.hours')}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('projectReports.timeTracking.billable')}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                  {formatHours(timeData.nonBillableTime)} {t('projectReports.timeTracking.hours')}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {t('projectReports.timeTracking.nonBillable')}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tasks by Status & Priority */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-4">
          {/* By Status */}
          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('projectReports.tasks.byStatus')}
            </h3>
            <div className="space-y-3">
              {Object.entries(tasksByStatus).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                    {t(`tasks.status.${status}`)}
                  </span>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* By Priority */}
          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('projectReports.tasks.byPriority')}
            </h3>
            <div className="space-y-3">
              {Object.entries(tasksByPriority).map(([priority, count]) => (
                <div key={priority} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                    {t(`tasks.priority.${priority}`)}
                  </span>
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg p-4 text-white">
            <h3 className="text-lg font-semibold mb-4">
              {t('projectReports.overview.title')}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between pb-2 border-b border-white/20">
                <span className="text-sm opacity-90">{t('projectReports.overview.totalProjects')}</span>
                <span className="text-xl font-bold">{projectStats.totalProjects}</span>
              </div>
              <div className="flex items-center justify-between pb-2 border-b border-white/20">
                <span className="text-sm opacity-90">{t('projectReports.overview.totalTasks')}</span>
                <span className="text-xl font-bold">{projectStats.totalTasks}</span>
              </div>
              <div className="flex items-center justify-between pb-2 border-b border-white/20">
                <span className="text-sm opacity-90">{t('projectReports.overview.completionRate')}</span>
                <span className="text-xl font-bold">{projectStats.completionRate.toFixed(1)}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm opacity-90">{t('projectReports.overview.overdueTasks')}</span>
                <span className="text-xl font-bold">{projectStats.overdueTasks}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Projects */}
        {topProjects.length > 0 && (
          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('projectReports.timeTracking.topProjects')}
            </h3>
            <div className="space-y-3">
              {topProjects.map((project, index) => (
                <div key={project.projectId} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-6">
                      {index + 1}.
                    </span>
                    <span className="text-sm text-gray-900 dark:text-white ml-2">
                      {project.projectName}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                    {project.count} {t('projectReports.tasks.title').toLowerCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Számlázható összesítés */}
        {billableSummary.rows.length > 0 && (
          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-4">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-xl"
              onClick={() => setShowBillableSummary(!showBillableSummary)}
            >
              <div className="flex items-center gap-3">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Számlázható összesítés
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({billableSummary.rows.length} task, összesen {billableSummary.totalLabel})
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); copyBillableSummary(); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
                  title="Tabulátoros formátum vágólapra"
                >
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  Vágólapra
                </button>
                {showBillableSummary ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                )}
              </div>
            </div>

            {showBillableSummary && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Feladat</th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Leírás</th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300 text-right">Óra (óra:perc)</th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300 text-right">Óra (tizedes)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {billableSummary.rows.map((row) => (
                      <tr key={row.taskNumber} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-3 py-2 font-mono text-gray-700 dark:text-gray-300">{row.taskNumber}</td>
                        <td className="px-3 py-2 text-gray-900 dark:text-white">{row.title}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700 dark:text-gray-300">{row.hours}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-gray-900 dark:text-white">{row.decimalHours}</td>
                      </tr>
                    ))}
                    <tr className="bg-green-50 dark:bg-green-900/10 font-semibold">
                      <td className="px-3 py-2 text-gray-900 dark:text-white" colSpan={2}>Összesen</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-900 dark:text-white">{billableSummary.totalLabel}</td>
                      <td className="px-3 py-2 text-right font-mono text-green-700 dark:text-green-300">{billableSummary.totalDecimal}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Időbejegyzések részletezése — flat lista */}
        {flatTimeEntries.length > 0 && (
          <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-4">
            <div
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-xl"
              onClick={() => setShowTimeEntriesSummary(!showTimeEntriesSummary)}
            >
              <div className="flex items-center gap-3">
                <ClockIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Időbejegyzések részletezése
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({flatTimeEntries.length} db)
                </span>
              </div>
              <div className="flex items-center gap-2">
                {flatTimeEntries.some(({ entry }) => !entry.isBillable) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); bulkSetAllBillable(); }}
                    disabled={bulkUpdating}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-800/50 text-green-700 dark:text-green-300 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Minden látható időbejegyzést számlázhatóra állít"
                  >
                    {bulkUpdating ? (
                      <span className="animate-pulse">...</span>
                    ) : (
                      <>
                        <CurrencyDollarIcon className="h-3.5 w-3.5" />
                        Mindet számlázhatóra
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); copyTimeEntriesSummary(); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
                  title="Tabulátoros formátum vágólapra"
                >
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                  Vágólapra
                </button>
                {showTimeEntriesSummary ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                )}
              </div>
            </div>

            {showTimeEntriesSummary && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Feladat</th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Leírás</th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Dátum / Idő</th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300 text-right">Időtartam</th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Számlázható</th>
                      <th className="px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Felhasználó</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {flatTimeEntries.map(({ entry, task }) => {
                      const start = entry.startTime?.toDate ? entry.startTime.toDate() : new Date(entry.startTime);
                      const end = entry.endTime?.toDate ? entry.endTime.toDate() : (entry.endTime ? new Date(entry.endTime) : null);
                      const duration = entry.duration || 0;
                      const hours = Math.floor(duration / 3600);
                      const mins = Math.floor((duration % 3600) / 60);
                      return (
                        <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-3 py-2">
                            <div className="font-mono text-xs text-gray-500 dark:text-gray-400">{task.taskNumber || task.id.slice(0, 8)}</div>
                            <div className="text-gray-900 dark:text-white text-xs">{task.title}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs max-w-xs truncate" title={entry.description || ''}>
                            {entry.description || '-'}
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs whitespace-nowrap">
                            {format(start, 'MM.dd HH:mm', { locale: hu })}
                            {end && ` → ${format(end, 'HH:mm', { locale: hu })}`}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-gray-900 dark:text-white">
                            {hours}h {mins}m
                          </td>
                          <td className="px-3 py-2">
                            <button
                              onClick={() => toggleBillable(entry.id, !entry.isBillable, task.id)}
                              disabled={updatingEntryId === entry.id}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                                entry.isBillable
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200'
                                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 hover:bg-yellow-200'
                              }`}
                            >
                              {updatingEntryId === entry.id ? (
                                <span className="animate-pulse">...</span>
                              ) : (
                                entry.isBillable ? 'Számlázható ✓' : 'Ellenőrzés alatt'
                              )}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs">
                            {entry.userName || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Detailed Task List */}
        <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Task List Header */}
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-t-xl"
            onClick={() => setShowTaskList(!showTaskList)}
          >
            <div className="flex items-center gap-3">
              <DocumentTextIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Részletes feladatlista
              </h3>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                ({filteredTaskList.length} feladat)
              </span>
            </div>
            {showTaskList ? (
              <ChevronUpIcon className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 text-gray-500" />
            )}
          </div>

          {/* Task List Content */}
          {showTaskList && (
            <div className="border-t border-gray-200 dark:border-gray-700">
              {filteredTaskList.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Nincs megjeleníthető feladat a kiválasztott szűrőkkel
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          Feladat
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          Projekt
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          Státusz
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          Prioritás
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          Felelős
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          Határidő
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                          Órák
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredTaskList.map((task) => {
                        const getStatusColor = (status: Task['status']) => {
                          switch (status) {
                            case 'todo': return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200';
                            case 'in_progress': return 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300';
                            case 'completed': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
                            case 'cancelled': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
                            default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200';
                          }
                        };

                        const getPriorityColor = (priority: Task['priority']) => {
                          switch (priority) {
                            case 'low': return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
                            case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300';
                            case 'high': return 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300';
                            case 'urgent': return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
                            default: return 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200';
                          }
                        };

                        const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';

                        const isExpanded = expandedTaskId === task.id;
                        const entries = taskTimeEntries.get(task.id) || [];
                        const periodEntries = dateRange
                          ? entries.filter(e => {
                              const entryDate = e.startTime?.toDate ? e.startTime.toDate() : new Date(e.startTime);
                              return entryDate >= dateRange.from && entryDate <= dateRange.to;
                            })
                          : entries;

                        return (
                          <React.Fragment key={task.id}>
                            <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isExpanded ? 'bg-blue-50 dark:bg-blue-900/10' : ''}`}>
                              <td className="px-4 py-3">
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                                      {task.taskNumber}
                                    </span>
                                    {entries.length > 0 && (
                                      <button
                                        onClick={() => setExpandedTaskId(isExpanded ? null : task.id)}
                                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${
                                          isExpanded
                                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                            : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 hover:bg-purple-200'
                                        }`}
                                        title={`${entries.length} időbejegyzés`}
                                      >
                                        <EyeIcon className="h-3 w-3 mr-0.5" />
                                        {entries.length}
                                      </button>
                                    )}
                                  </div>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                                    {task.title}
                                  </span>
                                  {task.tags && task.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {task.tags.slice(0, 3).map((tag: string, idx: number) => (
                                        <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
                                          {tag}
                                        </span>
                                      ))}
                                      {task.tags.length > 3 && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">+{task.tags.length - 3}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs md:text-sm text-gray-700 dark:text-gray-300">
                                  {task.projectName || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                                  {t(`tasks.status.${task.status}`)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                                  {t(`tasks.priority.${task.priority}`)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs md:text-sm text-gray-700 dark:text-gray-300">
                                  {task.assignedToName || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {task.dueDate ? (
                                  <div className="flex flex-col">
                                    <span className={`text-sm ${isOverdue ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>
                                      {format(new Date(task.dueDate), 'yyyy.MM.dd', { locale: hu })}
                                    </span>
                                    {isOverdue && (
                                      <span className="text-xs text-red-600 dark:text-red-400">
                                        Lejárt
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400 dark:text-gray-500">-</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col text-xs">
                                  <span className="text-gray-700 dark:text-gray-300">
                                    Becsült: {task.estimatedHours ? `${Number(task.estimatedHours).toFixed(1)}h` : '-'}
                                  </span>
                                  <span className="text-purple-600 dark:text-purple-400 font-medium">
                                    Tényleges: {(task.periodActualHours !== undefined ? task.periodActualHours : task.actualHours) ? `${Number(task.periodActualHours !== undefined ? task.periodActualHours : task.actualHours).toFixed(1)}h` : '-'}
                                  </span>
                                </div>
                              </td>
                            </tr>
                            {isExpanded && periodEntries.length > 0 && (
                              <tr className="bg-blue-50/50 dark:bg-blue-900/5">
                                <td colSpan={7} className="px-4 py-3">
                                  <div className="ml-4 border-l-2 border-blue-300 dark:border-blue-700 pl-4">
                                    <h4 className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2 flex items-center">
                                      <ClockIcon className="h-3.5 w-3.5 mr-1" />
                                      Időbejegyzések ({periodEntries.length} db)
                                    </h4>
                                    <div className="space-y-1.5">
                                      {periodEntries.map((entry: any) => {
                                        const start = entry.startTime?.toDate ? entry.startTime.toDate() : new Date(entry.startTime);
                                        const end = entry.endTime?.toDate ? entry.endTime.toDate() : (entry.endTime ? new Date(entry.endTime) : null);
                                        const duration = entry.duration || 0;
                                        const hours = Math.floor(duration / 3600);
                                        const mins = Math.floor((duration % 3600) / 60);
                                        return (
                                          <div key={entry.id} className="text-xs bg-white dark:bg-gray-800 rounded px-3 py-1.5 border border-blue-100 dark:border-blue-800">
                                            <div className="flex items-center justify-between">
                                              <div className="flex items-center gap-3">
                                                <span className="text-gray-500 dark:text-gray-400 font-mono">
                                                  {format(start, 'MM.dd HH:mm', { locale: hu })}
                                                  {end && ` → ${format(end, 'HH:mm', { locale: hu })}`}
                                                </span>
                                                <button
                                                  onClick={() => toggleBillable(entry.id, !entry.isBillable, task.id)}
                                                  disabled={updatingEntryId === entry.id}
                                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    entry.isBillable
                                                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-200'
                                                      : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300 hover:bg-yellow-200'
                                                  }`}
                                                  title={entry.isBillable ? 'Kattints az ellenőrzés alatt állításhoz' : 'Kattints a számlázhatóra állításhoz'}
                                                >
                                                  {updatingEntryId === entry.id ? (
                                                    <span className="animate-pulse">...</span>
                                                  ) : (
                                                    entry.isBillable ? 'Számlázható ✓' : 'Ellenőrzés alatt'
                                                  )}
                                                </button>
                                              </div>
                                              <div className="flex items-center gap-3">
                                                {entry.userName && (
                                                  <span className="text-gray-500 dark:text-gray-400">{entry.userName}</span>
                                                )}
                                                <span className="font-mono font-medium text-blue-700 dark:text-blue-300">
                                                  {hours}h {mins}m
                                                </span>
                                              </div>
                                            </div>
                                            {entry.description && (
                                              <div className="mt-1 text-gray-600 dark:text-gray-300 text-[11px] border-t border-gray-100 dark:border-gray-700 pt-1">
                                                {entry.description}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <div className="mt-2 flex items-center justify-between text-xs border-t border-blue-200 dark:border-blue-800 pt-2">
                                      <span className="text-gray-500 dark:text-gray-400">Összesített idő a szűrt időszakban:</span>
                                      <span className="font-mono font-bold text-blue-700 dark:text-blue-300">
                                        {(() => {
                                          const total = periodEntries.reduce((sum: number, e: any) => sum + (e.duration || 0), 0);
                                          const h = Math.floor(total / 3600);
                                          const m = Math.floor((total % 3600) / 60);
                                          return `${h}h ${m}m`;
                                        })()}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectReports;
